import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IPoolState } from "./types";
import { IIDAStore } from "../types";
import { IPoolPost, IPoolPut, Pool } from "../../models/pools";
import { poolService } from "../../services/Pool.service";
import store from "../../store";
import { WorkHourPoolStatus } from "../../models/workHourPoolStatus";
import { StoreCache, storeCachePeriode } from "../storeCache";
import { IPeriode } from "@/utils/dateUtils";
import { startOfDay, endOfDay } from "date-fns";

export const getInitialState = (): IPoolState => {
  return {
    searching: false,
    pools: [],
    workHourPoolStatus: new WorkHourPoolStatus(),
    storeCache: new StoreCache(),
    workHourPoolCache: new StoreCache()
  };
};

const namespaced = true;

export const getters: GetterTree<IPoolState, IIDAStore> = {
  GetPools: (state): Pool[] => {
    return state.pools;
  },
  GetPoolsByEmploymentIds:
    (state) =>
      (employmentIds: number[]): Pool[] => {
        const list: Pool[] = state.pools.filter(
          (p) => employmentIds.find((id) => id === p.employment_id) !== undefined
        );
        return list;
      },
  GetWorkHourPoolStatus: (state): WorkHourPoolStatus => {
    return state.workHourPoolStatus;
  }
};

export const actions: ActionTree<IPoolState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchPools({ commit, state }): Promise<Pool[]> {
    return new Promise((resolve, reject) => {
      if (state.pools.length === 0) {
        try {
          poolService.fetchPools().then((pools) => {
            commit("updatePools", pools);
            return resolve(pools);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        const pools = state.pools;
        return resolve(pools);
      }
    });
  },
  fetchPoolsByEmploymentId({ commit }, employmentId: number): Promise<Pool[]> {
    return new Promise((resolve, reject) => {
      try {
        poolService.fetchPoolsByEmploymentId(employmentId).then((pools) => {
          commit("updatePools", pools);
          return resolve(pools);
        });
      } catch (error) {
        return reject(error);
      }
    });
  },
  createPool({ commit }, pool: Pool): Promise<Pool> {
    return new Promise((resolve, reject) => {
      poolService
        .createPool(pool)
        .then((newPool) => {
          commit("insertPool", newPool);
          resolve(newPool);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updatePool({ commit }, pool: Pool): Promise<Pool> {
    return new Promise((resolve, reject) => {
      poolService
        .updatePool(pool)
        .then((updatedPool) => {
          commit("updatePool", updatedPool);
          resolve(updatedPool);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createUpdatePools(
    { commit },
    obj: { pools_to_create: IPoolPost[]; pools_to_update: IPoolPut[] }
  ): Promise<{ createdPools: Pool[]; updatedPools: Pool[]; errors: string[] }> {
    return new Promise((resolve, reject) => {
      poolService
        .createUpdatePools(obj)
        .then(
          (data: {
            createdPools: Pool[];
            updatedPools: Pool[];
            errors: string[];
          }) => {
            if (data.createdPools.length > 0) {
              commit("updatePools", data.createdPools);
            }
            if (data.updatedPools.length > 0) {
              commit("updatePools", data.updatedPools);
            }
            resolve(data);
          }
        )
        .catch((error) => {
          reject(error);
        });
    });
  },
  deletePool({ commit }, poolId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      poolService
        .deletePool(poolId)
        .then((response) => {
          commit("deletePool", poolId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getPoolRestTimeByDate(
    { commit, state },
    obj: { date: Date; forceUpdate: boolean }
  ): Promise<WorkHourPoolStatus> {
    // The pool status for a date only contains pools whose employment is active
    // on that date, so it must be cached per exact date. Caching it under the
    // full span of the active employments is unsafe: when an employment starts
    // mid-span (e.g. a new norm period on 1/7) a later date has a different set
    // of active employments and pools, yet falls inside the already-cached span
    // and is wrongly served stale data — hiding the new pools. See SFOP-7379.
    const dateRange: IPeriode = {
      fromDate: startOfDay(obj.date),
      toDate: endOfDay(obj.date)
    };
    return new Promise((resolve, reject) => {
      if (
        !state.workHourPoolCache.isCached(dateRange) ||
        obj.forceUpdate
      ) {
        poolService
          .getPoolStatus(obj.date)
          .then((response: WorkHourPoolStatus) => {
            commit("updatePools", response.work_hour_pools);
            commit("updatePoolStatus", {
              workHourPoolStatus: response,
              dateRange: dateRange
            });
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        return resolve(state.workHourPoolStatus);
      }
    });
  }
};

export const mutations: MutationTree<IPoolState> = {
  reset(state: IPoolState) {
    state.storeCache.resetCache();
    Object.assign(state, getInitialState());
  },
  insertPool(state: IPoolState, pool: Pool) {
    state.pools.push(pool);
    state.storeCache.resetCache();
  },
  updatePool(state: IPoolState, pool: Pool) {
    const index = state.pools.findIndex((d) => d.id === pool.id);
    state.pools.splice(index, 1);
    state.pools.push(pool);
    state.storeCache.resetCache();
  },
  deletePool(state: IPoolState, poolId: number) {
    const index = state.pools.findIndex((d) => d.id === poolId);
    state.pools.splice(index, 1);
    state.storeCache.resetCache();
  },
  updatePools(state: IPoolState, pools: Pool[]) {
    pools.forEach((pool: Pool) => {
      const index = state.pools.findIndex((d) => d.id === pool.id);
      if (index > -1) {
        state.pools.splice(index, 1);
        state.pools.push(pool);
      } else {
        state.pools.push(pool);
      }
    });
    state.storeCache.resetCache();
  },
  updatePoolStatus(
    state: IPoolState,
    payload: { workHourPoolStatus: WorkHourPoolStatus; dateRange: IPeriode }
  ) {
    state.workHourPoolStatus = payload.workHourPoolStatus;
    state.workHourPoolCache.addCache(
      new storeCachePeriode({ fromDate: payload.dateRange.fromDate, toDate: payload.dateRange.toDate })
    );
  }
};

export const poolStoreModule: Module<IPoolState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
